import { toast } from "sonner";
import { trpc } from "@/lib/trpc";
import type { HomeySnapshot } from "@/types/homey";

/**
 * Mutation + optimistic cache patch for device capability commands.
 *
 * Optimistic patching: immediately updates the snapshot in the React Query
 * cache (same technique as the socket delta handler in useHomey) before the
 * network round-trip completes. The websocket delta from Homey will
 * confirm/correct the value once the physical device responds.
 *
 * On error: shows a sonner toast and invalidates the snapshot query so the
 * next fetch re-syncs from the server.
 */
export function useDeviceCommands() {
  const utils = trpc.useUtils();

  const mutation = trpc.homey.setCapability.useMutation({
    onError(err) {
      toast.error(`Command failed: ${err.message}`);
      void utils.homey.getSnapshot.invalidate();
    },
  });

  /**
   * Patch a single device capability in the cached snapshot immediately.
   *
   * This is a per-capability merge: only the one [capabilityId] key is
   * replaced inside capabilitiesObj — all other capabilities and device fields
   * are shallow-cloned unchanged. This means an incoming WebSocket delta for a
   * different capability will not clobber our optimistic value, and an incoming
   * delta for the same capability may briefly override it until Homey confirms
   * the command. A full pending-mutation ignore-window is deferred (the value
   * self-heals on the next correct delta).
   */
  function patchSnapshot(deviceId: string, capabilityId: string, value: unknown) {
    utils.homey.getSnapshot.setData(undefined, (old: HomeySnapshot | undefined) => {
      if (!old) return old;
      return {
        ...old,
        devices: old.devices.map((d) => {
          if (d.id !== deviceId) return d;
          const existing = d.capabilitiesObj[capabilityId];
          return {
            ...d,
            capabilitiesObj: {
              ...d.capabilitiesObj,
              [capabilityId]: {
                id: capabilityId,
                value,
                title: existing?.title,
                units: existing?.units,
              },
            },
          };
        }),
      };
    });
  }

  function toggle(deviceId: string, currentActive: boolean) {
    const newValue = !currentActive;
    patchSnapshot(deviceId, "onoff", newValue);
    mutation.mutate({ deviceId, capabilityId: "onoff", value: newValue });
  }

  function dim(_deviceId: string, _value: number) {
    // onChange fires at 60 Hz during a drag. The DimmerSlider already tracks the
    // visual position via its internal `latest.current` ref while dragging, so
    // patching the React Query cache here would trigger a full buildZoneSections
    // rebuild on every pointermove event (79+ devices) with no visual benefit.
    // Nothing to do — onCommit (commitDim) applies the single optimistic patch.
  }

  function commitDim(deviceId: string, value: number) {
    // onCommit — one optimistic patch + network mutation on pointer-up.
    patchSnapshot(deviceId, "dim", value);
    mutation.mutate({ deviceId, capabilityId: "dim", value });
  }

  function commitTargetTemperature(deviceId: string, value: number) {
    patchSnapshot(deviceId, "target_temperature", value);
    mutation.mutate({ deviceId, capabilityId: "target_temperature", value });
  }

  // -- Media commands ------------------------------------------------------

  /**
   * Fire a momentary button capability (speaker_next/prev, remote_*,
   * volume_up/down, button.restart). These are triggered by setting the
   * capability to `true`; Homey treats them as fire-and-forget, so there is no
   * optimistic state to patch.
   */
  function pressMomentary(deviceId: string, capabilityId: string) {
    mutation.mutate({ deviceId, capabilityId, value: true });
  }

  /**
   * Toggle a boolean capability (volume_mute, speaker_shuffle, or
   * speaker_playing when it doubles as the play/pause target). Optimistically
   * patches the snapshot so the UI flips immediately.
   */
  function setBoolean(deviceId: string, capabilityId: string, value: boolean) {
    patchSnapshot(deviceId, capabilityId, value);
    mutation.mutate({ deviceId, capabilityId, value });
  }

  /** Commit a volume_set value (0..1) on pointer-up; mirrors commitDim. */
  function commitVolume(deviceId: string, value: number) {
    patchSnapshot(deviceId, "volume_set", value);
    mutation.mutate({ deviceId, capabilityId: "volume_set", value });
  }

  return {
    toggle,
    dim,
    commitDim,
    commitTargetTemperature,
    pressMomentary,
    setBoolean,
    commitVolume,
  };
}
